// src/pages/UnlabelledStockReport.jsx
import React, { useMemo, useState } from "react";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiLayers,
  FiX,
} from "react-icons/fi";
import Select from "react-select";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetCountersAllQuery } from "../toolkitServices/counterApi";
import { useGetUnlabelledDailyReportQuery } from "../toolkitServices/reportApi";

import { handleDropDowns } from "../config/dropDownHelpers";

const TZ = "Asia/Kolkata";

const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const todayYMD = () => {
  const d = new Date();
  const y = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
  }).format(d);
  const m = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    month: "2-digit",
  }).format(d);
  const dd = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    day: "2-digit",
  }).format(d);
  return `${y}-${m}-${dd}`;
};

const fmtRangeHuman = (range) => {
  if (!range?.start || !range?.end) return "";
  const s = new Date(range.start);
  const e = new Date(range.end);
  const opt = {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return `${s.toLocaleString("en-IN", opt)} — ${e.toLocaleString("en-IN", opt)} IST`;
};

const toNum = (v) => Number(v ?? 0) || 0;
const fmt = (n, d = 3) =>
  Number.isFinite(Number(n)) ? Number(n).toFixed(d) : "0.000";
const fmt0 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(0) : "0");
const nonEmpty = (s) => typeof s === "string" && s.trim().length > 0;

const findOpt = (opts, value) =>
  (opts || []).find((o) => String(o.value) === String(value)) || null;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-2 sm:px-3 py-2 text-xs sm:text-xs";
const metricGroupCell = (index) =>
  index === 0
    ? "border-l-2 border-l-slate-300"
    : "border-l border-l-slate-200/70";

function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-500 hover:text-red-500"
        title="Clear"
      >
        ×
      </button>
    </span>
  );
}

function MetricMiniCard({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="mt-0.5 text-[13px] font-semibold text-slate-800">
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon ? <Icon className="text-[#6b59c6] text-sm" /> : null}
      <div className="text-xs font-semibold text-slate-800">{children}</div>
    </div>
  );
}

function MobileRowCard({ row, showMovement }) {
  return (
    <div className="border rounded-2xl bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b bg-[#f7f4ff]">
        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-slate-400">Branch</div>
            <div className="font-semibold text-slate-800">
              {row?.branch?.name || "-"}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Counter</div>
            <div className="font-semibold text-slate-800">
              {row?.counter?.name || "-"}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Category</div>
            <div className="font-semibold text-slate-800">
              {row?.category?.name || "-"}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Product</div>
            <div className="font-semibold text-slate-800">
              {row?.product?.name || "-"}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Design</div>
            <div className="font-semibold text-slate-800">
              {row?.design?.name || "-"}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Purity</div>
            <div className="font-semibold text-slate-800">
              {row?.purity?.name || "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <SectionLabel icon={FiLayers}>Opening</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <MetricMiniCard
              label="Qty"
              value={fmt0(toNum(row?.opening?.qty))}
            />
            <MetricMiniCard
              label="Gross"
              value={fmt(toNum(row?.opening?.grossWt))}
            />
            <MetricMiniCard
              label="Net"
              value={fmt(toNum(row?.opening?.netWt))}
            />
            <MetricMiniCard
              label="Stone"
              value={fmt(toNum(row?.opening?.stoneWt))}
            />
            <div className="col-span-2">
              <MetricMiniCard
                label="Diamond"
                value={fmt(toNum(row?.opening?.diamondWt))}
              />
            </div>
          </div>
        </div>

        {showMovement && (
          <>
            <div>
              <SectionLabel icon={FiArrowDownLeft}>IN</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard label="Qty" value={fmt0(toNum(row?.in?.qty))} />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.in?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.in?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.in?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.in?.diamondWt))}
                  />
                </div>
              </div>
            </div>

            <div>
              <SectionLabel icon={FiArrowUpRight}>OUT</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard
                  label="Qty"
                  value={fmt0(toNum(row?.out?.qty))}
                />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.out?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.out?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.out?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.out?.diamondWt))}
                  />
                </div>
              </div>
            </div>
            <div>
              <SectionLabel>EST</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard
                  label="Qty"
                  value={fmt0(toNum(row?.est?.qty))}
                />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.est?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.est?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.est?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.est?.diamondWt))}
                  />
                </div>
              </div>
            </div>
            <div>
              <SectionLabel>Return</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard
                  label="Qty"
                  value={fmt0(toNum(row?.["return"]?.qty))}
                />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.["return"]?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.["return"]?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.["return"]?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.["return"]?.diamondWt))}
                  />
                </div>
              </div>
            </div>

            <div>
              <SectionLabel>Transfer In</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard
                  label="Qty"
                  value={fmt0(toNum(row?.transferIn?.qty))}
                />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.transferIn?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.transferIn?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.transferIn?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.transferIn?.diamondWt))}
                  />
                </div>
              </div>
            </div>

            <div>
              <SectionLabel>Transfer Out</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <MetricMiniCard
                  label="Qty"
                  value={fmt0(toNum(row?.transferOut?.qty))}
                />
                <MetricMiniCard
                  label="Gross"
                  value={fmt(toNum(row?.transferOut?.grossWt))}
                />
                <MetricMiniCard
                  label="Net"
                  value={fmt(toNum(row?.transferOut?.netWt))}
                />
                <MetricMiniCard
                  label="Stone"
                  value={fmt(toNum(row?.transferOut?.stoneWt))}
                />
                <div className="col-span-2">
                  <MetricMiniCard
                    label="Diamond"
                    value={fmt(toNum(row?.transferOut?.diamondWt))}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div>
          <SectionLabel icon={FiLayers}>Closing</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <MetricMiniCard
              label="Qty"
              value={fmt0(toNum(row?.closing?.qty))}
            />
            <MetricMiniCard
              label="Gross"
              value={fmt(toNum(row?.closing?.grossWt))}
            />
            <MetricMiniCard
              label="Net"
              value={fmt(toNum(row?.closing?.netWt))}
            />
            <MetricMiniCard
              label="Stone"
              value={fmt(toNum(row?.closing?.stoneWt))}
            />
            <div className="col-span-2">
              <MetricMiniCard
                label="Diamond"
                value={fmt(toNum(row?.closing?.diamondWt))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SkeletonRows = ({ cols = 16, rows = 8 }) => {
  const widths = ["w-10", "w-20", "w-28", "w-16", "w-24", "w-32"];

  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`sk-${r}`} className="animate-pulse">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={`sk-${r}-${c}`} className={dataCell}>
              <div
                className={`h-3 ${widths[c % widths.length]} bg-slate-200 rounded`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

export default function UnlabelledStockReport() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [dateMode, setDateMode] = useState("day");
  const [date, setDate] = useState(todayYMD());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showMovement, setShowMovement] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    branchId: "",
    counterId: "",
    categoryId: "",
    productId: "",
    designId: "",
    purityId: "",
  });

  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();
  const { data: counters } = useGetCountersAllQuery();

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branches?.data || [] }),
    [branches],
  );

  const categoryOptions = useMemo(
    () =>
      handleDropDowns({ key: "category", categories: categories?.data || [] }),
    [categories],
  );

  const selectedBranch = useMemo(
    () => findOpt(branchOptions, filters.branchId),
    [branchOptions, filters.branchId],
  );

  const selectedCategory = useMemo(
    () => findOpt(categoryOptions, filters.categoryId),
    [categoryOptions, filters.categoryId],
  );

  const allCounterOptions = useMemo(() => {
    const list = counters?.data || counters || [];
    return (Array.isArray(list) ? list : []).map((x) => ({
      value: x?._id || x?.id || x?.counterId,
      label: x?.counterName || x?.name || x?.CounterName || "Counter",
      branchId: x?.branchId || x?.BranchId || x?.branch?._id || x?.Branch?._id,
    }));
  }, [counters]);

  const counterOptions = useMemo(() => {
    if (!filters.branchId) return allCounterOptions;
    return allCounterOptions.filter(
      (c) => String(c.branchId || "") === String(filters.branchId),
    );
  }, [allCounterOptions, filters.branchId]);

  const selectedCounter = useMemo(
    () => findOpt(counterOptions, filters.counterId),
    [counterOptions, filters.counterId],
  );

  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: products?.data || [],
        category: selectedCategory,
      }),
    [products, selectedCategory],
  );

  const selectedProduct = useMemo(
    () => findOpt(productOptions, filters.productId),
    [productOptions, filters.productId],
  );

  const designOptions = useMemo(
    () =>
      handleDropDowns({
        key: "design",
        designs: designs?.data || [],
        product: selectedProduct,
      }),
    [designs, selectedProduct],
  );

  const selectedDesign = useMemo(
    () => findOpt(designOptions, filters.designId),
    [designOptions, filters.designId],
  );

  const purityOptions = useMemo(
    () =>
      handleDropDowns({
        key: "purity",
        purities: purities?.data || [],
        category: selectedCategory,
      }),
    [purities, selectedCategory],
  );

  const selectedPurity = useMemo(
    () => findOpt(purityOptions, filters.purityId),
    [purityOptions, filters.purityId],
  );

  const queryArgs = useMemo(() => {
    const base = {
      movement: showMovement,
      branchId: filters.branchId || undefined,
      counterId: filters.counterId || undefined,
      categoryId: filters.categoryId || undefined,
      productId: filters.productId || undefined,
      designId: filters.designId || undefined,
      purityId: filters.purityId || undefined,
      tz: TZ,
    };

    if (dateMode === "range" && nonEmpty(from) && nonEmpty(to)) {
      return { ...base, from, to };
    }

    return { ...base, date };
  }, [showMovement, filters, dateMode, date, from, to]);

  const { data, isFetching, isError, error } = useGetUnlabelledDailyReportQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const rows = data?.rows || [];
  const grand = data?.grandTotal || {};
  const humanRange = fmtRangeHuman(data?.range);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = [
        r?.branch?.name || "",
        r?.counter?.name || "",
        r?.category?.name || "",
        r?.product?.name || "",
        r?.design?.name || "",
        r?.purity?.name || "",
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, search]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const visibleRows = filteredRows.slice(start, start + limit);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const clearSingleFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: "" };

      if (key === "branchId") next.counterId = "";
      if (key === "categoryId") {
        next.productId = "";
        next.designId = "";
        next.purityId = "";
      }
      if (key === "productId") {
        next.designId = "";
      }

      return next;
    });
    setPage(1);
  };

  const onBranchChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      branchId: opt?.value || "",
      counterId: "",
    }));
    setPage(1);
  };

  const onCounterChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      counterId: opt?.value || "",
    }));
    setPage(1);
  };

  const onCategoryChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      categoryId: opt?.value || "",
      productId: "",
      designId: "",
      purityId: "",
    }));
    setPage(1);
  };

  const onProductChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      productId: opt?.value || "",
      designId: "",
    }));
    setPage(1);
  };

  const onDesignChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      designId: opt?.value || "",
    }));
    setPage(1);
  };

  const onPurityChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      purityId: opt?.value || "",
    }));
    setPage(1);
  };

  const exportCSV = () => {
    const head = [
      "Branch",
      "Counter",
      "Category",
      "Product",
      "Design",
      "Purity",

      "Opening Qty",
      "Opening Gross",
      "Opening Net",
      "Opening Stone",
      "Opening Diamond",

      ...(showMovement
        ? [
            "IN Qty",
            "IN Gross",
            "IN Net",
            "IN Stone",
            "IN Diamond",

            "OUT Qty",
            "OUT Gross",
            "OUT Net",
            "OUT Stone",
            "OUT Diamond",

            "EST Qty",
            "EST Gross",
            "EST Net",
            "EST Stone",
            "EST Diamond",

            "RETURN Qty",
            "RETURN Gross",
            "RETURN Net",
            "RETURN Stone",
            "RETURN Diamond",

            "TRANSFER IN Qty",
            "TRANSFER IN Gross",
            "TRANSFER IN Net",
            "TRANSFER IN Stone",
            "TRANSFER IN Diamond",

            "TRANSFER OUT Qty",
            "TRANSFER OUT Gross",
            "TRANSFER OUT Net",
            "TRANSFER OUT Stone",
            "TRANSFER OUT Diamond",
          ]
        : []),

      "Closing Qty",
      "Closing Gross",
      "Closing Net",
      "Closing Stone",
      "Closing Diamond",
    ];

    const lines = [head.join(",")];

    const pushPack = (pack, cell) => {
      cell.push(fmt0(toNum(pack?.qty)));
      cell.push(fmt(toNum(pack?.grossWt)));
      cell.push(fmt(toNum(pack?.netWt)));
      cell.push(fmt(toNum(pack?.stoneWt)));
      cell.push(fmt(toNum(pack?.diamondWt)));
    };

    filteredRows.forEach((r) => {
      const cell = [
        (r?.branch?.name || "").replaceAll(",", " "),
        (r?.counter?.name || "").replaceAll(",", " "),
        (r?.category?.name || "").replaceAll(",", " "),
        (r?.product?.name || "").replaceAll(",", " "),
        (r?.design?.name || "").replaceAll(",", " "),
        (r?.purity?.name || "").replaceAll(",", " "),
      ];

      pushPack(r?.opening, cell);

      if (showMovement) {
        pushPack(r?.in, cell);
        pushPack(r?.out, cell);
        pushPack(r?.est, cell);
        pushPack(r?.["return"], cell);
        pushPack(r?.transferIn, cell);
        pushPack(r?.transferOut, cell);
      }

      pushPack(r?.closing, cell);
      lines.push(cell.join(","));
    });

    const footer = ["GRAND", "", "", "", "", ""];
    pushPack(grand?.opening, footer);

    if (showMovement) {
      pushPack(grand?.in, footer);
      pushPack(grand?.out, footer);
      pushPack(grand?.est, footer);
      pushPack(grand?.["return"], footer);
      pushPack(grand?.transferIn, footer);
      pushPack(grand?.transferOut, footer);
    }

    pushPack(grand?.closing, footer);
    lines.push(footer.join(","));

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp =
      dateMode === "range" && from && to ? `${from}_to_${to}` : date;
    a.download = `unlabelled-stock-report_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* loader overlay */}
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading unlabelled stock report…</span>
          </div>
        </div>
      )}

      {/* top header */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight">
            Unlabelled Stock Report
          </h2>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="hidden md:flex items-center gap-2 text-[11px] text-violet-100/90">
          {humanRange ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10">
              {humanRange}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10">
              Daily view
            </span>
          )}
        </div>
      </div>

      {/* filters bar */}
      <div className="border-b bg-slate-50/80 px-2 py-2 sm:px-4">
        <div className="flex flex-wrap items-end gap-2 sm:gap-3">
          {/* view */}
          <div className="hidden">
            <label className="text-[11px] font-medium text-slate-600">
              View
            </label>
            <div className="flex rounded-full overflow-hidden border border-slate-200 bg-white text-[11px]">
              <button
                type="button"
                onClick={() => {
                  setDateMode("day");
                  setPage(1);
                }}
                className={`flex-1 px-3 py-1.5 rounded-full font-medium transition ${
                  dateMode === "day"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                Day
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateMode("range");
                  setPage(1);
                }}
                className={`flex-1 px-3 py-1.5 rounded-full font-medium transition ${
                  dateMode === "range"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                Range
              </button>
            </div>
          </div>

          {/* date */}
          {dateMode === "day" ? (
            <div className="hidden">
              <label className="text-[11px] font-medium text-slate-600">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
            </div>
          ) : (
            <>
              <div className="hidden">
                <label className="text-[11px] font-medium text-slate-600">
                  From
                </label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
              </div>
              <div className="hidden">
                <label className="text-[11px] font-medium text-slate-600">
                  To
                </label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
              </div>
            </>
          )}

          {/* movement */}
          <div className="order-2 flex w-full flex-col gap-[1px] lg:order-none lg:w-[150px]">
            <label className="hidden text-[11px] font-medium text-slate-600 lg:block">
              Movement
            </label>
            <button
              type="button"
              onClick={() => setShowMovement((v) => !v)}
              className={`h-8 rounded-xl border px-3 text-[10px] font-medium transition lg:rounded-full lg:text-[11px] ${
                showMovement
                  ? "bg-[#6b59c6]/10 text-[#6b59c6] border-violet-200"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {showMovement ? "Movement ON" : "Movement OFF"}
            </button>
          </div>

          {/* search */}
          <div className="order-1 flex min-w-0 flex-1 flex-col gap-[1px] lg:order-none lg:min-w-[220px]">
            <label className="hidden text-[11px] font-medium text-slate-600 lg:block">
              Search
            </label>
            <div className="flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[11px] shadow-sm lg:h-8 lg:rounded-lg lg:shadow-none">
              <FiSearch className="text-slate-400" />
              <input
                type="text"
                placeholder="Branch, counter, category, product, design, purity…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent outline-none text-gray-800"
              />
            </div>
          </div>

          {/* actions */}
          <div className="order-1 flex w-auto flex-col gap-[1px] lg:order-none">
            <label className="hidden text-[11px] font-medium text-slate-600 lg:block lg:invisible">
              Actions
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex h-9 w-9 items-center justify-center gap-1 rounded-xl border border-violet-200 bg-white text-[#6b59c6] shadow-sm hover:bg-violet-50 lg:h-8 lg:w-auto lg:rounded-full lg:px-3 lg:shadow-none"
                title="Filters"
                aria-label="Advanced filters"
              >
                <FiFilter />
                <span className="hidden text-[11px] lg:inline">Filters</span>
              </button>

              <button
                type="button"
                onClick={exportCSV}
                disabled={!filteredRows.length}
                className="inline-flex h-9 w-9 items-center justify-center gap-1 rounded-xl bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 lg:h-8 lg:w-auto lg:rounded-full lg:px-3"
                title="Export CSV"
                aria-label="Export CSV"
              >
                <FiDownload />
                <span className="hidden text-[11px] lg:inline">Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* expanded filters */}
        {showFilters && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[1px] lg:hidden"
              onClick={() => setShowFilters(false)}
              aria-label="Close advanced filters"
            />
            <div className="fixed inset-x-3 bottom-3 z-50 max-h-[calc(100vh-110px)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl lg:static lg:mt-3 lg:max-h-none lg:overflow-visible lg:p-3 lg:shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-800">
                    Advanced Filters
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-500">
                    Choose the report period and inventory filters.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 lg:hidden"
                  aria-label="Close advanced filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="mb-3 rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                <div className="mb-2 text-[11px] font-semibold text-slate-700">
                  Report period
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:items-end">
                  <div>
                    <label className="mb-1 block text-[10px] text-slate-500">
                      View
                    </label>
                    <div className="flex h-9 rounded-lg border border-slate-200 bg-white p-0.5 text-[10px]">
                      <button
                        type="button"
                        onClick={() => {
                          setDateMode("day");
                          setPage(1);
                        }}
                        className={`flex-1 rounded-md font-semibold ${
                          dateMode === "day"
                            ? "bg-violet-100 text-[#5947aa]"
                            : "text-slate-500"
                        }`}
                      >
                        Day
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDateMode("range");
                          setPage(1);
                        }}
                        className={`flex-1 rounded-md font-semibold ${
                          dateMode === "range"
                            ? "bg-violet-100 text-[#5947aa]"
                            : "text-slate-500"
                        }`}
                      >
                        Range
                      </button>
                    </div>
                  </div>

                  {dateMode === "day" ? (
                    <label className="min-w-0 text-[10px] text-slate-500">
                      Date
                      <input
                        type="date"
                        value={date}
                        onChange={(event) => {
                          setDate(event.target.value);
                          setPage(1);
                        }}
                        className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none"
                      />
                    </label>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <label className="min-w-0 text-[10px] text-slate-500">
                        From
                        <input
                          type="date"
                          value={from}
                          onChange={(event) => {
                            setFrom(event.target.value);
                            setPage(1);
                          }}
                          className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none"
                        />
                      </label>
                      <label className="min-w-0 text-[10px] text-slate-500">
                        To
                        <input
                          type="date"
                          value={to}
                          onChange={(event) => {
                            setTo(event.target.value);
                            setPage(1);
                          }}
                          className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none"
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Branch
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={onBranchChange}
                    placeholder="Select branch"
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Counter
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={counterOptions}
                    value={selectedCounter}
                    onChange={onCounterChange}
                    placeholder="Select counter"
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Category
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={onCategoryChange}
                    placeholder="Select category"
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Product
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={productOptions}
                    value={selectedProduct}
                    onChange={onProductChange}
                    placeholder="Select product"
                    isDisabled={!filters.categoryId}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Design
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={designOptions}
                    value={selectedDesign}
                    onChange={onDesignChange}
                    placeholder="Select design"
                    isDisabled={!filters.productId}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-[11px] text-slate-600">
                    Purity
                  </label>
                  <Select
                    isClearable
                    styles={selectStyles}
                    options={purityOptions}
                    value={selectedPurity}
                    onChange={onPurityChange}
                    placeholder="Select purity"
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* chips */}
      {(filters.branchId ||
        filters.counterId ||
        filters.categoryId ||
        filters.productId ||
        filters.designId ||
        filters.purityId ||
        search) && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {selectedBranch && (
            <FilterChip
              label={`Branch: ${selectedBranch.label}`}
              onRemove={() => clearSingleFilter("branchId")}
            />
          )}
          {selectedCounter && (
            <FilterChip
              label={`Counter: ${selectedCounter.label}`}
              onRemove={() => clearSingleFilter("counterId")}
            />
          )}
          {selectedCategory && (
            <FilterChip
              label={`Category: ${selectedCategory.label}`}
              onRemove={() => clearSingleFilter("categoryId")}
            />
          )}
          {selectedProduct && (
            <FilterChip
              label={`Product: ${selectedProduct.label}`}
              onRemove={() => clearSingleFilter("productId")}
            />
          )}
          {selectedDesign && (
            <FilterChip
              label={`Design: ${selectedDesign.label}`}
              onRemove={() => clearSingleFilter("designId")}
            />
          )}
          {selectedPurity && (
            <FilterChip
              label={`Purity: ${selectedPurity.label}`}
              onRemove={() => clearSingleFilter("purityId")}
            />
          )}
          {search && (
            <FilterChip
              label={`Search: ${search}`}
              onRemove={() => setSearch("")}
            />
          )}
        </div>
      )}

      {/* error */}
      {isError && (
        <div className="mx-4 mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.data?.msg || error?.data?.message || "Failed to load report"}
        </div>
      )}

      {/* stock table — same horizontal list on every screen size */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto bg-white">
        <table className="min-w-[2200px] w-full text-xs sm:text-sm text-left">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-[10px] sm:text-[11px] sticky top-0 z-10">
            <tr>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Branch
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Counter
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Category
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Product
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Design
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Purity
              </th>

              <th
                colSpan={5}
                className={`${headerCell} border-l-2 border-violet-300 bg-violet-100 text-center text-violet-800`}
              >
                Opening
              </th>
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-emerald-300 bg-emerald-100 text-center text-emerald-800`}
                >
                  IN
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-rose-300 bg-rose-100 text-center text-rose-800`}
                >
                  OUT
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-amber-300 bg-amber-100 text-center text-amber-800`}
                >
                  EST
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-sky-300 bg-sky-100 text-center text-sky-800`}
                >
                  RETURN
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-teal-300 bg-teal-100 text-center text-teal-800`}
                >
                  TRANSFER IN
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={5}
                  className={`${headerCell} border-l-2 border-orange-300 bg-orange-100 text-center text-orange-800`}
                >
                  TRANSFER OUT
                </th>
              )}
              <th
                colSpan={5}
                className={`${headerCell} border-l-2 border-indigo-300 bg-indigo-100 text-center text-indigo-800`}
              >
                Closing
              </th>
            </tr>

            <tr className="bg-[#f2effa] text-slate-700">
              {["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                <th
                  key={`o-${h}`}
                  className={`${headerCell} ${metricGroupCell(index)} text-right`}
                >
                  {h}
                </th>
              ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`i-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`out-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`est-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`r-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`ti-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {showMovement &&
                ["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                  <th
                    key={`to-${h}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {h}
                  </th>
                ))}

              {["Qty", "Gross", "Net", "Stone", "Diamond"].map((h, index) => (
                <th
                  key={`c-${h}`}
                  className={`${headerCell} ${metricGroupCell(index)} text-right`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {isFetching && rows.length > 0 ? (
              <SkeletonRows cols={showMovement ? 46 : 16} rows={8} />
            ) : !visibleRows.length && !isError ? (
              <tr>
                <td
                  colSpan={showMovement ? 41 : 16}
                  className="px-4 py-10 text-center text-xs text-slate-500"
                >
                  No records found. Try changing filters or search text.
                </td>
              </tr>
            ) : (
              visibleRows.map((r, index) => (
                <tr
                  key={`${index}-${r?.branch?.name || ""}-${r?.product?.name || ""}`}
                  className="border-t hover:bg-[#f4f0ff] transition"
                >
                  <td className={dataCell}>{r?.branch?.name || "-"}</td>
                  <td className={dataCell}>{r?.counter?.name || "-"}</td>
                  <td className={dataCell}>{r?.category?.name || "-"}</td>
                  <td className={dataCell}>{r?.product?.name || "-"}</td>
                  <td className={dataCell}>{r?.design?.name || "-"}</td>
                  <td className={dataCell}>{r?.purity?.name || "-"}</td>

                  <td
                    className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                  >
                    {fmt0(toNum(r?.opening?.qty))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.opening?.grossWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.opening?.netWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.opening?.stoneWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.opening?.diamondWt))}
                  </td>

                  {showMovement && (
                    <>
                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.in?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.in?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.in?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.in?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.in?.diamondWt))}
                      </td>

                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.out?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.out?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.out?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.out?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.out?.diamondWt))}
                      </td>

                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.est?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.est?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.est?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.est?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.est?.diamondWt))}
                      </td>

                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.["return"]?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.["return"]?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.["return"]?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.["return"]?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.["return"]?.diamondWt))}
                      </td>

                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.transferIn?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferIn?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferIn?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferIn?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferIn?.diamondWt))}
                      </td>

                      <td
                        className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                      >
                        {fmt0(toNum(r?.transferOut?.qty))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferOut?.grossWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferOut?.netWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferOut?.stoneWt))}
                      </td>
                      <td className={`${dataCell} text-right`}>
                        {fmt(toNum(r?.transferOut?.diamondWt))}
                      </td>
                    </>
                  )}

                  <td
                    className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                  >
                    {fmt0(toNum(r?.closing?.qty))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.closing?.grossWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.closing?.netWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.closing?.stoneWt))}
                  </td>
                  <td className={`${dataCell} text-right`}>
                    {fmt(toNum(r?.closing?.diamondWt))}
                  </td>
                </tr>
              ))
            )}
          </tbody>

          {rows.length > 0 && !isFetching && (
            <tfoot>
              <tr className="bg-[#f7f4ff] font-semibold text-slate-800 border-t">
                <td className={dataCell}>GRAND</td>
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />

                <td
                  className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                >
                  {fmt0(toNum(grand?.opening?.qty))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.opening?.grossWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.opening?.netWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.opening?.stoneWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.opening?.diamondWt))}
                </td>

                {showMovement && (
                  <>
                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.in?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.in?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.in?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.in?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.in?.diamondWt))}
                    </td>

                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.out?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.out?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.out?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.out?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.out?.diamondWt))}
                    </td>

                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.est?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.est?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.est?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.est?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.est?.diamondWt))}
                    </td>

                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.["return"]?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.["return"]?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.["return"]?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.["return"]?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.["return"]?.diamondWt))}
                    </td>

                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.transferIn?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferIn?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferIn?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferIn?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferIn?.diamondWt))}
                    </td>

                    <td
                      className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                    >
                      {fmt0(toNum(grand?.transferOut?.qty))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferOut?.grossWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferOut?.netWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferOut?.stoneWt))}
                    </td>
                    <td className={`${dataCell} text-right`}>
                      {fmt(toNum(grand?.transferOut?.diamondWt))}
                    </td>
                  </>
                )}

                <td
                  className={`${dataCell} border-l-2 border-l-slate-300 text-right`}
                >
                  {fmt0(toNum(grand?.closing?.qty))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.closing?.grossWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.closing?.netWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.closing?.stoneWt))}
                </td>
                <td className={`${dataCell} text-right`}>
                  {fmt(toNum(grand?.closing?.diamondWt))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Legacy cards are hidden; every breakpoint uses the table above. */}
      <div className="hidden">
        {isFetching && rows.length > 0 ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))
        ) : !visibleRows.length && !isError ? (
          <div className="flex items-center justify-center h-full text-xs text-slate-500">
            No records found. Try adjusting filters.
          </div>
        ) : (
          <>
            {visibleRows.map((row, index) => (
              <MobileRowCard
                key={`${index}-${row?.branch?.name || ""}-${row?.product?.name || ""}`}
                row={row}
                showMovement={showMovement}
              />
            ))}

            {rows.length > 0 && (
              <div className="border rounded-2xl bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b bg-[#f7f4ff]">
                  <div className="text-sm font-semibold text-[#4b3d91]">
                    Grand Total
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <SectionLabel icon={FiLayers}>Opening</SectionLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <MetricMiniCard
                        label="Qty"
                        value={fmt0(toNum(grand?.opening?.qty))}
                      />
                      <MetricMiniCard
                        label="Gross"
                        value={fmt(toNum(grand?.opening?.grossWt))}
                      />
                      <MetricMiniCard
                        label="Net"
                        value={fmt(toNum(grand?.opening?.netWt))}
                      />
                      <MetricMiniCard
                        label="Stone"
                        value={fmt(toNum(grand?.opening?.stoneWt))}
                      />
                      <div className="col-span-2">
                        <MetricMiniCard
                          label="Diamond"
                          value={fmt(toNum(grand?.opening?.diamondWt))}
                        />
                      </div>
                    </div>
                  </div>

                  {showMovement && (
                    <>
                      <div>
                        <SectionLabel icon={FiArrowDownLeft}>IN</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.in?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.in?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.in?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.in?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.in?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <SectionLabel icon={FiArrowUpRight}>OUT</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.out?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.out?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.out?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.out?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.out?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <SectionLabel>EST</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.est?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.est?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.est?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.est?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.est?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <SectionLabel>Return</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.["return"]?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.["return"]?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.["return"]?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.["return"]?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.["return"]?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <SectionLabel>Transfer In</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.transferIn?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.transferIn?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.transferIn?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.transferIn?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.transferIn?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <SectionLabel>Transfer Out</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <MetricMiniCard
                            label="Qty"
                            value={fmt0(toNum(grand?.transferOut?.qty))}
                          />
                          <MetricMiniCard
                            label="Gross"
                            value={fmt(toNum(grand?.transferOut?.grossWt))}
                          />
                          <MetricMiniCard
                            label="Net"
                            value={fmt(toNum(grand?.transferOut?.netWt))}
                          />
                          <MetricMiniCard
                            label="Stone"
                            value={fmt(toNum(grand?.transferOut?.stoneWt))}
                          />
                          <div className="col-span-2">
                            <MetricMiniCard
                              label="Diamond"
                              value={fmt(toNum(grand?.transferOut?.diamondWt))}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <SectionLabel icon={FiLayers}>Closing</SectionLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <MetricMiniCard
                        label="Qty"
                        value={fmt0(toNum(grand?.closing?.qty))}
                      />
                      <MetricMiniCard
                        label="Gross"
                        value={fmt(toNum(grand?.closing?.grossWt))}
                      />
                      <MetricMiniCard
                        label="Net"
                        value={fmt(toNum(grand?.closing?.netWt))}
                      />
                      <MetricMiniCard
                        label="Stone"
                        value={fmt(toNum(grand?.closing?.stoneWt))}
                      />
                      <div className="col-span-2">
                        <MetricMiniCard
                          label="Diamond"
                          value={fmt(toNum(grand?.closing?.diamondWt))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {visibleRows.length ? start + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {visibleRows.length ? start + visibleRows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> entries
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 20);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={!canPrev || isFetching}
              onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <FiChevronLeft />
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: totalPages }, (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {totalPages}</span>
              </span>
              <span className="hidden sm:inline">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={!canNext || isFetching}
              onClick={() =>
                canNext && setPage((p) => Math.min(totalPages, p + 1))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
